// This file is auto-generated by bin/generate_country_configs.sh
// Do not edit manually. Re-run the generator when country configurations change.

import mapValues from 'lodash-es/mapValues';

import { COUNTRY_CODE } from '@rover/constants/src/geography/geo.constants';
import { union } from '@rover/utilities/regex';

// use these for i18n
export const ACTIVE_COUNTRY_CODES = [
  COUNTRY_CODE.US,
  COUNTRY_CODE.CA,
  COUNTRY_CODE.GB,
  COUNTRY_CODE.DE,
  COUNTRY_CODE.ES,
  COUNTRY_CODE.FR,
  COUNTRY_CODE.IT,
  COUNTRY_CODE.NL,
  COUNTRY_CODE.NO,
  COUNTRY_CODE.SE,
  COUNTRY_CODE.DK,
  COUNTRY_CODE.IE,
  COUNTRY_CODE.PL,
  COUNTRY_CODE.CH,
  COUNTRY_CODE.FI,
  COUNTRY_CODE.AT,
  COUNTRY_CODE.BE,
];

// use these for routes
export const ACTIVE_COUNTRY_URL_PREFIXES = [
  'us',
  'ca',
  'uk',
  'de',
  'es',
  'fr',
  'it',
  'nl',
  'no',
  'se',
  'dk',
  'ie',
  'pl',
  'ch',
  'fi',
  'at',
  'be',
] as const;
export type CountryUrlPrefix = (typeof ACTIVE_COUNTRY_URL_PREFIXES)[number];

const COUNTRY_CODE_BY_URL_PREFIX = {
  us: 'US',
  ca: 'CA',
  uk: 'GB',
  de: 'DE',
  es: 'ES',
  fr: 'FR',
  it: 'IT',
  nl: 'NL',
  no: 'NO',
  se: 'SE',
  dk: 'DK',
  ie: 'IE',
  pl: 'PL',
  ch: 'CH',
  fi: 'FI',
  at: 'AT',
  be: 'BE',
} as const;
type CountryCode = (typeof COUNTRY_CODE_BY_URL_PREFIX)[CountryUrlPrefix];

export const COUNTRY_URL_PREFIXES_REGEX_PATTERN = union(...ACTIVE_COUNTRY_URL_PREFIXES);
export const COUNTRY_URL_PREFIXES_REGEX = new RegExp(`^(${COUNTRY_URL_PREFIXES_REGEX_PATTERN})$`);

function isCountryUrlPrefix(countryUrlPrefix: string): countryUrlPrefix is CountryUrlPrefix {
  return COUNTRY_URL_PREFIXES_REGEX.test(countryUrlPrefix);
}

export function getCountryUrlPrefix(path?: string): CountryUrlPrefix | null {
  const countryUrlPrefix = path?.split('/')[1] ?? '';
  if (isCountryUrlPrefix(countryUrlPrefix)) {
    return countryUrlPrefix;
  }
  return null;
}

export const US_COUNTRY_URL_PREFIX: CountryUrlPrefix = 'us';
export const CA_COUNTRY_URL_PREFIX: CountryUrlPrefix = 'ca';

const LANGUAGES_BY_COUNTRY: Record<CountryCode, readonly string[]> = {
  US: ['en-us'],
  CA: ['en-ca', 'fr-ca'],
  GB: [
    'en-gb',
    'da-dk',
    'de-de',
    'es-es',
    'fr-fr',
    'it-it',
    'nl-nl',
    'nb-no',
    'pl-pl',
    'fi-fi',
    'sv-se',
  ],
  DE: [
    'de-de',
    'en-gb',
    'da-dk',
    'es-es',
    'fr-fr',
    'it-it',
    'nl-nl',
    'nb-no',
    'pl-pl',
    'fi-fi',
    'sv-se',
  ],
  ES: [
    'es-es',
    'en-gb',
    'da-dk',
    'de-de',
    'fr-fr',
    'it-it',
    'nl-nl',
    'nb-no',
    'pl-pl',
    'fi-fi',
    'sv-se',
  ],
  FR: [
    'fr-fr',
    'en-gb',
    'da-dk',
    'de-de',
    'es-es',
    'it-it',
    'nl-nl',
    'nb-no',
    'pl-pl',
    'fi-fi',
    'sv-se',
  ],
  IT: [
    'it-it',
    'en-gb',
    'da-dk',
    'de-de',
    'es-es',
    'fr-fr',
    'nl-nl',
    'nb-no',
    'pl-pl',
    'fi-fi',
    'sv-se',
  ],
  NL: [
    'nl-nl',
    'en-gb',
    'da-dk',
    'de-de',
    'es-es',
    'fr-fr',
    'it-it',
    'nb-no',
    'pl-pl',
    'fi-fi',
    'sv-se',
  ],
  NO: [
    'nb-no',
    'en-gb',
    'da-dk',
    'de-de',
    'es-es',
    'fr-fr',
    'it-it',
    'nl-nl',
    'pl-pl',
    'fi-fi',
    'sv-se',
  ],
  SE: [
    'sv-se',
    'en-gb',
    'da-dk',
    'de-de',
    'es-es',
    'fr-fr',
    'it-it',
    'nl-nl',
    'nb-no',
    'pl-pl',
    'fi-fi',
  ],
  DK: [
    'da-dk',
    'en-gb',
    'de-de',
    'es-es',
    'fr-fr',
    'it-it',
    'nl-nl',
    'nb-no',
    'pl-pl',
    'fi-fi',
    'sv-se',
  ],
  IE: [
    'en-ie',
    'da-dk',
    'de-de',
    'es-es',
    'fr-fr',
    'it-it',
    'nl-nl',
    'nb-no',
    'pl-pl',
    'fi-fi',
    'sv-se',
  ],
  PL: [
    'pl-pl',
    'en-gb',
    'da-dk',
    'de-de',
    'es-es',
    'fr-fr',
    'it-it',
    'nl-nl',
    'nb-no',
    'fi-fi',
    'sv-se',
  ],
  CH: [
    'de-ch',
    'en-gb',
    'da-dk',
    'es-es',
    'fr-fr',
    'it-it',
    'nl-nl',
    'nb-no',
    'pl-pl',
    'fi-fi',
    'sv-se',
  ],
  FI: [
    'fi-fi',
    'en-gb',
    'da-dk',
    'de-de',
    'es-es',
    'fr-fr',
    'it-it',
    'nl-nl',
    'nb-no',
    'pl-pl',
    'sv-se',
  ],
  AT: [
    'de-at',
    'en-gb',
    'da-dk',
    'es-es',
    'fr-fr',
    'it-it',
    'nl-nl',
    'nb-no',
    'pl-pl',
    'fi-fi',
    'sv-se',
  ],
  BE: [
    'nl-be',
    'en-gb',
    'da-dk',
    'de-de',
    'es-es',
    'fr-fr',
    'it-it',
    'nb-no',
    'pl-pl',
    'fi-fi',
    'sv-se',
  ],
};
const SHORT_LANGUAGES_BY_COUNTRY: Record<CountryCode, readonly string[]> = mapValues(
  LANGUAGES_BY_COUNTRY,
  (langs) => langs.map((lang) => lang.split('-')[0])
);

// Emitted from each country's backend `default_language` rather than inferred
// from language ordering, so it stays correct regardless of how the languages
// list is sorted.
const DEFAULT_LANGUAGE_BY_COUNTRY: Record<CountryCode, string> = {
  US: 'en-us',
  CA: 'en-ca',
  GB: 'en-gb',
  DE: 'de-de',
  ES: 'es-es',
  FR: 'fr-fr',
  IT: 'it-it',
  NL: 'nl-nl',
  NO: 'nb-no',
  SE: 'sv-se',
  DK: 'da-dk',
  IE: 'en-ie',
  PL: 'pl-pl',
  CH: 'de-ch',
  FI: 'fi-fi',
  AT: 'de-at',
  BE: 'nl-be',
};

export function getShortLanguagesByUrlPrefix(urlPrefix: CountryUrlPrefix): readonly string[] {
  return SHORT_LANGUAGES_BY_COUNTRY[COUNTRY_CODE_BY_URL_PREFIX[urlPrefix]];
}

export function getDefaultLanguageByCountryCode(countryCode: string): string | undefined {
  return DEFAULT_LANGUAGE_BY_COUNTRY[countryCode as CountryCode];
}

// Canonical homepage paths (trailing slash form). Consumers that also need to
// match the no-trailing-slash variants should derive them at the call site.
export const HOMEPAGE_PATHS: readonly string[] = [
  '/',
  ...ACTIVE_COUNTRY_URL_PREFIXES.flatMap((prefix) => [
    `/${prefix}/`,
    ...getShortLanguagesByUrlPrefix(prefix).map((lang) => `/${prefix}/${lang}/`),
  ]),
];
